import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import TextField from "./TextField";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {
  IoInformationCircleOutline,
  IoInformationCircleSharp,
} from "react-icons/io5";
import countries from "./countries.json";
import Autocomplete from "./AutoComplete";
import { setdata } from "../../../../../../helpers/helpers";
import InputAdornment from "@mui/material/InputAdornment";
import { Button, Stack } from "@mui/material";
import { BsPlus, BsTrash } from "react-icons/bs";
import IconButton from "@mui/material/IconButton";
import Tooltip from "../../../../../../Components/Tooltip/Tooltip";

const ExportReasonTypes = [
  "permanent",
  "temporary",
  "return",
  "used_exhibition_goods_to_origin",
  "intercompany_use",
  "commercial_purpose_or_sale",
  "personal_belongings_or_personal_use",
  "sample",
  "gift",
  "return_to_origin",
  "warranty_replacement",
  "diplomatic_goods",
  "defence_material",
];

const initialData = {
  Description: "",
  Country: "",
  OriginCountryCode: "",
  TotalPriceAmount: "",
  CommodityCode: "",
  Count: "",
};

// ------------------ PRODUCT ROW ------------------
const ProductRow = ({
  product,
  index,
  Helper,
  handleProductChange,
  handleProductCountry,
  handleRemove,
  totalProducts,
}) => (
  <React.Fragment key={index}>
    <Grid container spacing={2} justifyContent="center" mb={2} mt={2}>
      <Grid item xs={12} md={6}>
        <TextField
          Label={`Units Of Product No [${index + 1}]`}
          Name="Count"
          Type="number"
          Data={product.Count}
          Handler={(e) => handleProductChange(e, index)}
          PlaceHolder="1"
          HelperText={
            Helper
              ? "This value should represent how many identical items are included in shipment."
              : ""
          }
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Autocomplete
          Option={countries.map((option) => option.label)}
          Handler={(value) => handleProductCountry(value, index)}
          Value={product.Country}
          Type="Country"
          HelperText={
            Helper ? "Country where the good was manufactured or grown." : ""
          }
        />
      </Grid>
    </Grid>

    <Grid container spacing={2} justifyContent="center" mb={2}>
      <Grid item xs={12} md={6}>
        <TextField
          Label="Commodity Code"
          Name="CommodityCode"
          Data={product.CommodityCode}
          Handler={(e) => handleProductChange(e, index)}
          PlaceHolder="Enter HS Code"
          Required={false}
          HelperText={Helper ? "Commodity Code of the product." : ""}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          Label="Declaration Value"
          Name="TotalPriceAmount"
          Type="number"
          Data={product.TotalPriceAmount}
          PlaceHolder="0"
          Handler={(e) => handleProductChange(e, index)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ color: "var(--color-brand)" }}>
                <b>
                  <small>
                    {product.TotalPriceAmount > 0 && product.Count > 0
                      ? product.TotalPriceAmount +
                        " x " +
                        product.Count +
                        " = " +
                        product.TotalPriceAmount * product.Count
                      : ""}{" "}
                    CAD
                  </small>
                </b>
              </InputAdornment>
            ),
          }}
          HelperText={
            Helper
              ? "The Declaration Value is the cost of one unit of this product. The final customs value is calculated as unit value × number of units."
              : ""
          }
        />
      </Grid>
    </Grid>

    <Grid container spacing={2} justifyContent="center" mb={2}>
      <Grid item xs={12} md={10}>
        <TextField
          Label="Description"
          Name="Description"
          Data={product.Description}
          Handler={(e) => handleProductChange(e, index)}
          HelperText={Helper ? "Description of the product." : ""}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        {totalProducts > 1 && (
          <Button
            variant="text"
            size="small"
            startIcon={<BsTrash size={14} />}
            onClick={() => handleRemove(index)}
          >
            Remove
          </Button>
        )}
      </Grid>
    </Grid>

    {index !== totalProducts - 1 && (
      <div
        style={{
          height: "2px",
          background: "var(--color-ui-border)",
          margin: "18px 0",
        }}
      ></div>
    )}
  </React.Fragment>
);

// ------------------ PRODUCER SECTION ------------------
const ProducerSection = ({
  producer,
  Helper,
  handleProducerChange,
  handleProducerCountry,
}) => (
  <>
    <small>
      <b>Producer Information:</b>
    </small>

    <Grid container spacing={2} justifyContent="center" mb={2} mt={2}>
      <Grid item xs={12} md={6}>
        <TextField
          Label="Company Name"
          Name="CompanyName"
          Data={producer.CompanyName}
          Required={false}
          Handler={handleProducerChange}
          HelperText={
            Helper ? "Company or Individual name of the Producer." : ""
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Autocomplete
          Option={countries.map((option) => option.label)}
          Handler={handleProducerCountry}
          Value={producer.Country}
          Type="Country"
          HelperText={Helper ? "Country of the Producer." : ""}
        />
      </Grid>
    </Grid>
  </>
);

// ------------------ MAIN COMPONENT ------------------
export default function InternationalForm({
  StepCheck,
  SetStepCheck,
  isInternational,
}) {
  const [Helper, setHelper] = useState(false);

  // ---------- HANDLERS ----------
  const updateProduct = (index, field, value) => {
    SetStepCheck((prev) => ({
      ...prev,
      International: {
        ...prev.International,
        products: prev.International.products.map((p, i) =>
          i === index ? { ...p, [field]: value } : p
        ),
      },
    }));
  };

  const handleProductChange = (e, index) =>
    updateProduct(index, e.target.name, e.target.value);

  const handleProductCountry = (value, index) => {
    SetStepCheck((prev) => ({
      ...prev,
      International: {
        ...prev.International,
        products: prev.International.products.map((p, i) =>
          i === index
            ? {
                ...p,
                Country: setdata(() => value, false, ""),
                OriginCountryCode: setdata(
                  () => countries.find((cn) => cn.label === value)?.code || "",
                  false,
                  ""
                ),
              }
            : p
        ),
      },
    }));
  };

  const handleAdd = () => {
    SetStepCheck((prev) => ({
      ...prev,
      International: {
        ...prev.International,
        products: [...(prev.International.products || []), { ...initialData }],
      },
    }));
  };

  const handleRemove = (index) => {
    SetStepCheck((prev) => ({
      ...prev,
      International: {
        ...prev.International,
        products: prev.International.products.filter((_, i) => i !== index),
      },
    }));
  };

  const handleProducerChange = (e) => {
    const { name, value } = e.target;
    SetStepCheck((prev) => ({
      ...prev,
      International: {
        ...prev.International,
        producer: { ...prev.International.producer, [name]: value },
      },
    }));
  };

  const handleProducerCountry = (value) => {
    SetStepCheck((prev) => ({
      ...prev,
      International: {
        ...prev.International,
        producer: {
          ...prev.International.producer,
          Country: setdata(() => value, false, ""),
          CountryCode: setdata(
            () => countries.find((cn) => cn.label === value)?.code || "",
            false,
            ""
          ),
        },
      },
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    SetStepCheck((prev) => ({
      ...prev,
      International: { ...prev.International, [name]: value },
    }));
  };

  const handleChangeByType = (value, column) => {
    SetStepCheck((prev) => ({
      ...prev,
      International: { ...prev.International, [column]: value },
    }));
  };

  // ---------- RENDER ----------
  if (!isInternational) return null;

  const products = StepCheck?.International?.products || [];
  const producer = StepCheck?.International?.producer || {};

  return (
    <Row className="mx-auto mt-3 mb-3">
      <Col md={11} className="mx-auto">
        <div className="card bg-ui-surface shadow wapper_like_shopify">
          <div className="card-header">
            <span>Customs Declaration Form</span>
            <Tooltip
              title={
                Helper === true
                  ? "Click To Hide Information"
                  : "Click To Show Information"
              }
            >
              <IconButton onClick={() => setHelper(!Helper)}>
                {Helper === true ? (
                  <IoInformationCircleSharp size={15} />
                ) : (
                  <IoInformationCircleOutline size={15} />
                )}
              </IconButton>
            </Tooltip>
          </div>

          <div className="card-body">
            <div className="package__type_main_wrap">
              {/* PRODUCTS SECTION */}
              <div className="package__type_package">
                <small>
                  <b>Products Information:</b>
                </small>

                {products.map((product, index) => (
                  <ProductRow
                    key={index}
                    product={product}
                    index={index}
                    Helper={Helper}
                    handleProductChange={handleProductChange}
                    handleProductCountry={handleProductCountry}
                    handleRemove={handleRemove}
                    totalProducts={products.length}
                  />
                ))}

                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="center"
                  mt={2}
                >
                  <Button
                    variant="text"
                    startIcon={<BsPlus />}
                    onClick={handleAdd}
                    className="dimension_weight_card_add_new_row"
                  >
                    Add new product
                  </Button>
                </Stack>
              </div>

              {/* PRODUCER SECTION */}
              <div className="package__divider" />
              <div className="package__type_pallet">
                <ProducerSection
                  producer={producer}
                  Helper={Helper}
                  handleProducerChange={handleProducerChange}
                  handleProducerCountry={handleProducerCountry}
                />

                {/* Additional Fields */}
                <div
                  style={{
                    height: "2px",
                    background: "var(--color-ui-border)",
                    margin: "18px 0",
                  }}
                ></div>
                <small>
                  <b>Additional Information:</b>
                </small>
                <Grid
                  container
                  spacing={2}
                  justifyContent="center"
                  mt={2}
                  mb={2}
                >
                  <Grid item xs={12} md={6}>
                    <TextField
                      Label="Insurance Amount"
                      Name="InsuranceAmount"
                      Type="number"
                      Data={StepCheck.International.InsuranceAmount}
                      Handler={handleChange}
                      HelperText={
                        Helper
                          ? "The amount the shipper or receiver pays to cover the cost of replacing the shipment if it is lost or damaged."
                          : ""
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      Option={ExportReasonTypes}
                      Handler={(value) =>
                        handleChangeByType(value, "ExportReasonType")
                      }
                      Value={StepCheck.International.ExportReasonType}
                      Type="Export Reason Types"
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} justifyContent="center" mb={2}>
                  <Grid item xs={12}>
                    <TextField
                      Label="Reason For Export"
                      Name="ReasonForExport"
                      Data={StepCheck.International.ReasonForExport}
                      Handler={handleChange}
                      HelperText={
                        Helper
                          ? "A reason to export the current international shipment. Valid values: SALE, GIFT, SAMPLE, RETURN, REPAIR, INTERCOMPANYDATA, Any other reason."
                          : ""
                      }
                    />
                  </Grid>
                </Grid>

                {/* Duties */}
                <div
                  style={{
                    height: "2px",
                    background: "var(--color-ui-border)",
                    margin: "18px 0",
                  }}
                ></div>
                <Grid container spacing={2} justifyContent="center" mb={2}>
                  <Grid item xs={12} md={6}>
                    <small>
                      <b>Who Will Pay Duties:</b>
                    </small>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      className="package__type_package_main_btn_wrap"
                    >
                      <span>
                        <Button
                          name="shipper"
                          size="small"
                          onClick={() =>
                            handleChangeByType("shipper", "duties_by")
                          }
                          disableElevation
                          className={
                            StepCheck?.International?.duties_by === "shipper"
                              ? "serviceBtnActive"
                              : "serviceBtnNotsActive"
                          }
                        >
                          Shipper
                        </Button>
                      </span>

                      <span>
                        <Button
                          name="receiver"
                          size="small"
                          onClick={() =>
                            handleChangeByType("receiver", "duties_by")
                          }
                          disableElevation
                          className={
                            StepCheck?.International?.duties_by === "receiver"
                              ? "serviceBtnActive"
                              : "serviceBtnNotsActive"
                          }
                        >
                          CONSIGNEE
                        </Button>
                      </span>
                    </Stack>
                  </Grid>
                </Grid>

                {StepCheck?.International?.duties_by === "shipper" && (
                  <Grid container justifyContent="center" mb={2}>
                    <Grid item xs={12}>
                      <span
                        style={{
                          color: "gray",
                          fontSize: "10px",
                          lineHeight: "1",
                          letterSpacing: "0",
                        }}
                      >
                        <small
                          style={{
                            lineHeight: "1.1",
                            letterSpacing: "0",
                            display: "block",
                          }}
                        >
                          Please note: You have selected the shipper to pay the
                          duties. Duty charges are not yet included in the
                          shipment total and will be billed separately once we
                          receive the final information from the carrier.
                        </small>
                      </span>
                    </Grid>
                  </Grid>
                )}
              </div>
            </div>
          </div>
        </div>
      </Col>
    </Row>
  );
}
